import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoUrl from '@/assets/eTaxiLogo.png';
import { ReportColumn, ReportValue, reportTotals, toReportText } from '@/misc/report';

/**
 * Branded PDF export for the admin tables, built on the same jspdf +
 * jspdf-autotable pair the Promoter payouts export already uses.
 *
 * Layout follows the backend's PdfFactory house style so these reports look
 * like the billing and invoice PDFs the system already sends out: logo top
 * right at 75pt wide, heading text on the left, table underneath.
 *
 * The logo is deliberately `assets/eTaxiLogo.png` — the black-backed variant.
 * The transparent-background version of the same artwork has a near-white
 * NAMIBIA wordmark (#E0E0E0, about 1.32:1 against white) which disappears
 * completely on a white page.
 */

// jsPDF defaults to millimetres on A4. The backend sizes this logo at 75pt.
const LOGO_WIDTH_MM = 26.5;
// Intrinsic aspect of eTaxiLogo.png (254 x 327), so it is never stretched.
const LOGO_ASPECT = 327 / 254;
const LOGO_HEIGHT_MM = LOGO_WIDTH_MM * LOGO_ASPECT;

const MARGIN_MM = 14;

/**
 * Past this many columns a portrait page squeezes the table unreadably, so
 * the report turns landscape instead. Rides and Drivers cross it; Billing
 * does not.
 */
const LANDSCAPE_COLUMN_THRESHOLD = 6;

/**
 * Loaded once and shared by every export. Mirrors the promise-wrapped loader
 * in `imageCompression.ts` — jsPDF needs a decoded image, not a URL.
 */
let logoPromise: Promise<HTMLImageElement> | null = null;

function loadLogo(): Promise<HTMLImageElement> {
    if (!logoPromise) {
        logoPromise = new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error('eTaxi logo could not be loaded'));
            image.src = logoUrl;
        });
    }
    return logoPromise;
}

export interface PdfReportOptions<T> {
    fileName: string,
    /** Report type, e.g. "Rides". Rendered large, top left. */
    title: string,
    /** Company and date range, rendered under the title. */
    subtitle: string,
    rows: T[],
    columns: ReportColumn<T>[]
}

function toCell(value: ReportValue): string {
    return toReportText(value);
}

export async function downloadTablePdf<T>(options: PdfReportOptions<T>): Promise<void> {
    const landscape = options.columns.length > LANDSCAPE_COLUMN_THRESHOLD;

    // jsPDF's own typings do not describe the constructor well enough to call
    // it directly, the same reason the Promoter export casts it.
    const JsPdfConstructor: any = jsPDF;
    const doc = new JsPdfConstructor({
        orientation: landscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();

    // The logo is best-effort: a broken image must not cost the user their
    // report, so fall back to a text-only header.
    try {
        const logo = await loadLogo();
        doc.addImage(
            logo, 'PNG',
            pageWidth - MARGIN_MM - LOGO_WIDTH_MM, MARGIN_MM,
            LOGO_WIDTH_MM, LOGO_HEIGHT_MM
        );
    } catch (e) {
        // Header text below still identifies the report.
    }

    doc.setFontSize(16);
    doc.setTextColor(20, 20, 20);
    doc.text(options.title, MARGIN_MM, MARGIN_MM + 8);

    doc.setFontSize(10);
    doc.setTextColor(90, 90, 90);
    doc.text(options.subtitle, MARGIN_MM, MARGIN_MM + 15);

    const hasTotals = options.columns.some((column) => column.total);
    let foot: string[][] | undefined;
    if (hasTotals) {
        const totals = reportTotals(options.rows, options.columns);
        let labelled = false;
        foot = [options.columns.map((column, index) => {
            if (totals[index] !== undefined) {
                return toCell(totals[index]);
            }
            if (!labelled) {
                labelled = true;
                return 'Total';
            }
            return '';
        })];
    }

    autoTable(doc, {
        head: [options.columns.map((column) => column.header)],
        body: options.rows.map((row) => options.columns.map((column) => toCell(column.value(row)))),
        foot,
        startY: MARGIN_MM + LOGO_HEIGHT_MM + 6,
        margin: { left: MARGIN_MM, right: MARGIN_MM },
        styles: { fontSize: 8, cellPadding: 1.5 },
        headStyles: { fillColor: [33, 33, 33] },
        footStyles: { fillColor: [235, 235, 235], textColor: [20, 20, 20], fontStyle: 'bold' }
    });

    doc.save(options.fileName);
}

/**
 * `Rides — eTaxi Office Test — 2026-08-01 to 2026-08-21`, with whichever
 * parts are actually known.
 */
export function reportSubtitle(company: string | null, from: string | null, to: string | null): string {
    const parts: string[] = [];
    if (company) {
        parts.push(company);
    }
    if (from && to) {
        parts.push(`${from} to ${to}`);
    }
    return parts.join('  |  ');
}
